import { useEffect, useState, Fragment } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';

interface Removal {
  id: string;
  account_id: string;
  marketplace: string;
  removal_order_id: string;
  sku: string;
  fnsku: string;
  asin: string;
  product_name: string;
  order_type: string;
  order_status: string;
  disposition: string;
  reason: string;
  condition: string;
  requested_qty: number;
  cancelled_qty: number;
  disposed_qty: number;
  shipped_qty: number;
  carrier: string;
  tracking_number: string;
  ship_city: string;
  ship_state: string;
  ship_postal: string;
  request_date: string;
  last_updated_date: string;
  ship_date: string;
  is_recommendation: number;
  recommended_removal_qty: number;
  available_qty: number;
  created_at: string;
  updated_at: string;
}

interface RemovalSummary {
  order_type: string;
  order_status: string;
  count: number;
  total_requested: number;
  total_shipped: number;
  total_disposed: number;
  total_cancelled: number;
}

const TYPE_COLORS: Record<string, string> = {
  Return: 'bg-blue-100 text-blue-700',
  Disposal: 'bg-red-100 text-red-700',
  Liquidation: 'bg-purple-100 text-purple-700',
};
const STATUS_COLORS: Record<string, string> = {
  Pending: 'bg-yellow-100 text-yellow-700',
  Processing: 'bg-blue-100 text-blue-700',
  Complete: 'bg-green-100 text-green-700',
  Cancelled: 'bg-gray-100 text-gray-500',
};

export function InventoryRemovals() {
  const [removals, setRemovals] = useState<Removal[]>([]);
  const [summary, setSummary] = useState<RemovalSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterSku, setFilterSku] = useState('');
  const { displayName, parentNickname } = useNicknames();
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRec, setFilterRec] = useState('0'); // default: show orders, not recommendations
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => { load(); }, [filterSku, filterType, filterStatus, filterRec]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterSku) params.set('sku', filterSku);
      if (filterType) params.set('order_type', filterType);
      if (filterStatus) params.set('order_status', filterStatus);
      if (filterRec) params.set('is_recommendation', filterRec);
      const [removalData, summaryData] = await Promise.all([
        api.get<{ removals: Removal[] }>(`/inventory/removals?${params}`),
        api.get<{ summary: RemovalSummary[] }>('/inventory/removals/summary'),
      ]);
      setRemovals(removalData.removals);
      setSummary(summaryData.summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  if (loading && removals.length === 0) return <LoadingSkeleton />;

  const totalRequested = summary.reduce((s, r) => s + r.total_requested, 0);
  const totalShipped = summary.reduce((s, r) => s + r.total_shipped, 0);
  const totalDisposed = summary.reduce((s, r) => s + r.total_disposed, 0);
  const totalCancelled = summary.reduce((s, r) => s + r.total_cancelled, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-base font-semibold">FBA Removals</h2>
        <p className="text-sm text-gray-500">Track removal orders, shipments, and disposal lifecycle.</p>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3"><p className="text-sm text-red-600">{error}</p></div>}

      {/* Summary Cards */}
      {summary.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard label="Requested" value={totalRequested} color="text-gray-900" />
          <SummaryCard label="Shipped (Return)" value={totalShipped} color="text-blue-600" />
          <SummaryCard label="Disposed" value={totalDisposed} color="text-red-600" />
          <SummaryCard label="Cancelled" value={totalCancelled} color="text-gray-500" />
        </div>
      )}

      {/* Breakdown by type/status */}
      {summary.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="text-xs font-semibold text-gray-500 mb-3">Breakdown by Type & Status</h3>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {summary.map((s) => (
              <div key={`${s.order_type}-${s.order_status}`} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_COLORS[s.order_type] ?? 'bg-gray-100 text-gray-600'}`}>
                    {s.order_type || '—'}
                  </span>
                  <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[s.order_status] ?? 'bg-gray-100 text-gray-500'}`}>
                    {s.order_status || '—'}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-700">{s.count} orders / {s.total_requested} units</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={filterSku}
          onChange={(e) => setFilterSku(e.target.value)}
          placeholder="Filter by SKU..."
          className="w-48 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">All Types</option>
          <option value="Return">Return</option>
          <option value="Disposal">Disposal</option>
          <option value="Liquidation">Liquidation</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">All Statuses</option>
          <option value="Pending">Pending</option>
          <option value="Processing">Processing</option>
          <option value="Complete">Complete</option>
          <option value="Cancelled">Cancelled</option>
        </select>
        <select
          value={filterRec}
          onChange={(e) => setFilterRec(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="0">Orders Only</option>
          <option value="1">Recommendations Only</option>
          <option value="">All</option>
        </select>
      </div>

      {/* Removals Table */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold mb-4">Removals ({removals.length})</h3>
        {removals.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No removal records found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Date</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Removal ID</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Product</th>
                  <th className="pb-2 text-center text-xs font-medium text-gray-500">Type</th>
                  <th className="pb-2 text-center text-xs font-medium text-gray-500">Status</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Disposition</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Destination</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Req</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Ship</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Disp</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Canc</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {removals.map((r) => {
                  const isExpanded = expandedId === r.id;
                  const destination = [r.ship_city, r.ship_state].filter(Boolean).join(', ');
                  return (
                    <Fragment key={r.id}>
                      <tr className={isExpanded ? 'bg-gray-50' : ''}>
                        <td className="py-2 text-gray-600 text-xs whitespace-nowrap">
                          {r.request_date ? r.request_date.slice(0, 10) : '—'}
                        </td>
                        <td className="py-2 font-mono text-xs text-gray-700 max-w-[120px] truncate" title={r.removal_order_id}>
                          {r.removal_order_id || (r.is_recommendation ? 'Recommendation' : '—')}
                        </td>
                        <td className="py-2 font-medium text-gray-900 max-w-[160px]" title={r.sku}>
                          {parentNickname(r.sku) && <span className="text-[10px] text-gray-400 block truncate">{parentNickname(r.sku)}</span>}
                          <span className="truncate block">{displayName(r.sku)}</span>
                        </td>
                        <td className="py-2 text-center">
                          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_COLORS[r.order_type] ?? 'bg-gray-100 text-gray-600'}`}>
                            {r.order_type || '—'}
                          </span>
                        </td>
                        <td className="py-2 text-center">
                          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[r.order_status] ?? 'bg-gray-100 text-gray-500'}`}>
                            {r.order_status || '—'}
                          </span>
                        </td>
                        <td className="py-2 text-gray-600 text-xs">{r.disposition || '—'}</td>
                        <td className="py-2 text-gray-600 text-xs max-w-[120px] truncate" title={destination}>{destination || '—'}</td>
                        <td className="py-2 text-right text-gray-700">{r.requested_qty}</td>
                        <td className="py-2 text-right text-blue-600 font-medium">{r.shipped_qty || '—'}</td>
                        <td className="py-2 text-right text-red-600 font-medium">{r.disposed_qty || '—'}</td>
                        <td className="py-2 text-right text-gray-400">{r.cancelled_qty || '—'}</td>
                        <td className="py-2 text-right">
                          <button
                            onClick={() => setExpandedId(isExpanded ? null : r.id)}
                            className="rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50"
                          >
                            {isExpanded ? 'Less' : 'More'}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={12} className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
                              <DetailLine label="ASIN" value={r.asin} />
                              <DetailLine label="Product" value={r.product_name} />
                              <DetailLine label="Reason" value={r.reason} />
                              <DetailLine label="Condition" value={r.condition} />
                              <DetailLine label="Ship Date" value={r.ship_date?.slice(0, 10)} />
                              <DetailLine label="Last Updated" value={r.last_updated_date?.slice(0, 10)} />
                              <DetailLine label="Carrier" value={r.carrier} />
                              <DetailLine label="Tracking" value={r.tracking_number} />
                              <DetailLine label="Destination" value={[r.ship_city, r.ship_state, r.ship_postal].filter(Boolean).join(', ')} />
                              {r.is_recommendation === 1 && (
                                <DetailLine label="Recommended Qty" value={String(r.recommended_removal_qty)} />
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}

function DetailLine({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex items-baseline justify-between rounded-lg bg-gray-50 px-3 py-2">
      <span className="text-gray-500 text-xs">{label}</span>
      <span className={`text-sm font-medium ${value ? 'text-gray-900' : 'text-gray-400'}`}>{value || '—'}</span>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div><div className="h-5 w-40 rounded bg-gray-200" /><div className="mt-2 h-4 w-64 rounded bg-gray-200" /></div>
      <div className="grid gap-3 sm:grid-cols-4"><div className="h-20 rounded-xl bg-gray-200" /><div className="h-20 rounded-xl bg-gray-200" /><div className="h-20 rounded-xl bg-gray-200" /><div className="h-20 rounded-xl bg-gray-200" /></div>
      <div className="h-64 rounded-xl bg-gray-200" />
    </div>
  );
}
